import React, { useState, useRef, useEffect } from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useFieldArray, useFormContext } from "react-hook-form";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import ReactDOM from "react-dom";
import useSelectedColorStore from "@store/register/useSelectedColor";

import NumberInput from "@components/input/NumberInput";
import { useTranslation } from "react-i18next";

const Container = styled.div({
  position: "relative",
  zIndex: 1,
});

const Button = styled.button<{ selectedValue: string }>(
  ({ theme, selectedValue }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.4rem",
    width: "100%",
    fontSize: "1.6rem",
    fontWeight: theme.fontWeight.regular,
    lineHeight: "2rem",
    padding: "1.6rem",
    border: `1px solid ${theme.color.light_gray}`,
    color:
      selectedValue === "선택" || selectedValue === "Select"
        ? theme.color.light_gray
        : theme.color.black,
  }),
);

const SelectBox = styled(motion.div)<{
  top: number;
  left: number;
  width: number;
}>(({ theme, top, left, width }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  position: "absolute",
  top: `${top}px`,
  left: `${left}px`,
  width: `${width}px`,
  backgroundColor: theme.color.white,
  border: `1px solid ${theme.color.light_gray}`,
  overflowY: "auto",
  maxHeight: "256px",
  zIndex: 10000,
}));

const OptionItem = styled.button<{ last: boolean; selected: boolean }>(
  ({ theme, last, selected }) => ({
    width: "100%",
    padding: "10px 30px 10px 20px",
    display: "flex",
    justifyContent: "space-between",
    gap: "1rem",
    alignItems: "center",
    fontSize: "1.6rem",
    lineHeight: "2.4rem",
    fontWeight: theme.fontWeight.regular,
    color: selected ? theme.color.green : theme.color.black,
    backgroundColor: theme.color.white,
    border: "none",
    textAlign: "start",
    borderBottom: last ? "none" : `1px solid ${theme.color.border_gray}`,
    ":hover": {
      backgroundColor: theme.color.border_gray,
    },
  }),
);

const ChangeQuantityContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  paddingTop: "1.6rem",
  gap: "1.6rem",
});

const QuantityBox = styled.div({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "1rem",
  padding: "0.5rem 1rem",
});

const QuantityInputBox = styled.div({
  display: "flex",
  gap: "1rem",
  alignItems: "center",
});

type Props = {
  options: any[];
  name: string;
  open: boolean;
  setOpen: (value: boolean) => void;
};

const SizeDropdown = ({ options = [], name, open, setOpen }: Props) => {
  const [focusName, setFocusName] = useState("");
  const { t } = useTranslation();
  const { control, watch } = useFormContext();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const theme = useTheme();
  const currentSizeList = watch(name) || [];
  const lang = window.localStorage.getItem("language");

  const { remove, append } = useFieldArray({
    control,
    name,
  }) as any;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setFocusName(name);
    if (options?.length === 0) return;

    setOpen(!open);
  };

  const handleChange = (sizeId: number, nameKo: string) => {
    const existingIndex = currentSizeList.findIndex(
      (item: any) => item.size_id === sizeId,
    );

    if (existingIndex !== -1) {
      remove(existingIndex);
    } else {
      append({ size_id: sizeId, amount: 0, size_name: nameKo });
    }
  };

  const handleDeleteSize = (index: number) => {
    remove(index);
  };

  useEffect(() => {
    if (focusName !== name) {
      setOpen(false);
    }
  }, [focusName]);

  useEffect(() => {
    if (open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [open]);

  return (
    <Container>
      <Button
        onClick={handleToggle}
        type="button"
        selectedValue={
          currentSizeList?.length > 0
            ? currentSizeList?.map((v: any) => v.size_name).join(", ")
            : t("선택")
        }
        ref={buttonRef}>
        {currentSizeList[currentSizeList.length - 1]?.size_name || t("선택")}
        <Image
          src={
            open
              ? theme.image.arrow_up_gray.default
              : theme.image.arrow_down_gray.default
          }
        />
      </Button>
      {open &&
        ReactDOM.createPortal(
          <SelectBox
            top={dropdownPos.top}
            left={dropdownPos.left}
            width={dropdownPos.width}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.1 }}>
            {options.map((item, index) => (
              <OptionItem
                key={index}
                type="button"
                last={index === options.length - 1}
                onClick={() =>
                  handleChange(
                    item.code,
                    lang === "en" && item.name_en ? item.name_en : item.name_ko,
                  )
                }
                selected={currentSizeList.some(
                  (field: any) => field.size_id === item.code,
                )}>
                {lang === "en" && item.name_en ? item.name_en : item.name_ko}

                {currentSizeList.some(
                  (field: any) => field.size_id === item.code,
                ) && <Image src={theme.image.check_green.default} />}
              </OptionItem>
            ))}
          </SelectBox>,
          document.body,
        )}

      <ChangeQuantityContainer>
        {currentSizeList.map((item: any, index: number) => (
          <QuantityBox key={index}>
            {item.size_name}
            <QuantityInputBox>
              <NumberInput
                name={`${name}.${index}.amount`}
                type="number"
                mode="numeric"
                maxWidth="10rem"
                dir="rtl"
                value={watch(`${name}.${index}.amount`)}
              />
              <span>{t("갯수")}</span>

              <button onClick={() => handleDeleteSize(index)} type="button">
                <Image src={theme.image.close_round.default} size="2rem" />
              </button>
            </QuantityInputBox>
          </QuantityBox>
        ))}
      </ChangeQuantityContainer>
    </Container>
  );
};

export default SizeDropdown;
